import React, { useState, useEffect, useMemo } from 'react';
import { object, func } from 'prop-types';
import { TableCustom, Loading, Modal } from '../../common';
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { ServerRequest } from '../../../utils/apiweb';
import { APIS } from '../../../config/apis';
import { getFormatNumber } from '../../../utils/convert';
import { useEntidad } from '../../hooks';
import { isNull } from '../../../utils/validator';

const CuentaPlanPagoDeudaOriginalGrid = (props) => {

    //hooks
    const [state, setState] = useState({
        loading: false
    });
    const [idTipoTributo, setIdTipoTributo] = useState(0);
    const [items, setItems] = useState([]);

    const mount = () => {
        if (props.data?.idPlanPago > 0) {
            setIdTipoTributo(props.data.idTipoTributo);
            SearchCuentaCorrienteItem(props.data.idPlanPago);
        }
    }
    useEffect(mount, [props.data.idPlanPago]);

    const [, getRowEntidad] = useEntidad({
        entidades: ['Tasa','SubTasa','TipoMovimiento','Rubro'],
        onLoaded: (entidades, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
            key: 'Tasa_SubTasa_Rubro',
            timeout: 0
        }
    });

    //definiciones
    const getCodigoTasa = (id) => {
        const row = getRowEntidad('Tasa', id);
        return (row) ? row.codigo : '';
    }
    const getDescSubTasa = (id) => {
        const row = getRowEntidad('SubTasa', id);
        return (row) ? `${row.codigo} - ${row.descripcion}` : '';
    }
    const getCodigoRubro = (id) => {
        const row = getRowEntidad('Rubro', id);
        return (row) ? row.codigo : '';
    }
    const getDescTipoMovimiento = (id) => {
        const row = getRowEntidad('TipoMovimiento', id);
        return (row) ? row.nombre : '';
    }
    const GetFooterTotal = (info, columnName) => {
        const total = useMemo(
            () =>
            info.rows.reduce((sum, row) => row.values[columnName] + sum, 0),
            [info.rows]
        )

        return <>{getFormatNumber(total,2)}</>
    }

    const columnRubro = useMemo(() =>
        (idTipoTributo === TRIBUTO_TYPE.COMERCIOS) ? [
            { Header: 'Rubro', Cell: (props) => !isNull(props.value) ? getCodigoRubro(props.value) : '', accessor: 'idRubro', width: '8%', disableSortBy: true, ellipsis: false}
        ] : []
    , [idTipoTributo]);

    const tableColumns = [
        { Header: 'Partida', accessor: 'numeroPartida', width: '8%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Tasa', Cell: (props) => getCodigoTasa(props.value), accessor: 'idTasa', width: '8%', disableSortBy: true, ellipsis: false},
        { Header: 'Sub tasa', Cell: (props) => getDescSubTasa(props.value), accessor: 'idSubTasa', width: (columnRubro.length ? '22%' : '30%'), disableSortBy: true},
        ...columnRubro,
        { Header: 'Período', accessor: 'periodo', width: '8%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Cuota', accessor: 'cuota', width: '7%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Movimiento', Cell: (props) => getDescTipoMovimiento(props.value), accessor: 'idTipoMovimiento', width: '15%', disableSortBy: true},
        { Header: 'Debe', Footer: (info) => GetFooterTotal(info ,'importeDebe'), Cell: (props) => getFormatNumber(props.value, 2), id: 'importeDebe', accessor: 'importeDebe', width: '12%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Haber', Footer: (info) => GetFooterTotal(info ,'importeHaber'), Cell: (props) => getFormatNumber(props.value, 2), id: 'importeHaber', accessor: 'importeHaber', width: '12%', alignCell: 'right', disableSortBy: true, ellipsis: false}
    ];

    // Orden: por numero de partida descendente (cada partida junta) y, dentro de cada partida,
    // por periodo y cuota ascendente. En la deuda original la fecha de movimiento es la de
    // conformacion (constante), por eso el desempate pasa a periodo/cuota.
    const sortDeudaOriginal = (a, b) => (b.numeroPartida !== a.numeroPartida)
        ? b.numeroPartida - a.numeroPartida
        : (a.periodo !== b.periodo)
            ? a.periodo.localeCompare(b.periodo)
            : a.cuota - b.cuota;

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
            const message = error.message;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
            return {...prevState, loading: false};
        });
    }
    const callbackSuccessSearchCuentaCorrienteItem = (response) => {
        response.json()
        .then((data) => {
            data.sort(sortDeudaOriginal);

            setItems(data);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    }

    // funciones
    function SearchCuentaCorrienteItem(idPlanPago) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const paramsUrl = `/plan-pago-deuda-original/${idPlanPago}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CUENTA_CORRIENTE_ITEM,
            paramsUrl,
            null,
            callbackSuccessSearchCuentaCorrienteItem,
            callbackNoSuccess,
            callbackError
        );
    }

    return (
    <>
        <Loading visible={state.loading}></Loading>

        <Modal
            onEscape={() => props.onDismiss(false)}
            size="xl"
            allowFullscreen={true}
            title={"Deuda original - Plan de pago N° " + (props.data?.numero > 0 ? props.data?.numero : '')}
            body={
                <TableCustom
                    className={'TableCustomBase'}
                    columns={tableColumns}
                    data={ items }
                    showFooter={true}
                    showFilterGlobal={false}
                />
            }
            footer={<div className="width-100p f-align">
                <button className="btn back-button" data-dismiss="modal" onClick={ (event) => props.onDismiss(false) }>Volver</button>
            </div>}
        />

    </>
    );
}

CuentaPlanPagoDeudaOriginalGrid.propTypes = {
    data: object.isRequired,
    onDismiss: func.isRequired
};

export default CuentaPlanPagoDeudaOriginalGrid;
